import Glide from '@glidejs/glide/dist/glide.esm';

export default (defaultProductId) => ({
  activeProduct: null,
  glideInstances: [],

  init() {
    // Listen for grid layout changes
    document.addEventListener(
      'grid-layout-changed',
      this.handleGridLayoutChange.bind(this),
    );

    // Initialize slider if defaultProductId is provided
    if (defaultProductId) {
      this.initSlider(defaultProductId);
    }
  },

  initSlider(productId) {
    // Check if we already have an instance for this product
    const existingInstance = this.getGlideInstance(`glide_${productId}`);

    if (existingInstance) {
      // If it exists, update it
      existingInstance.update();
      return;
    }

    // Otherwise create a new instance
    if (this.$refs[`glide_${productId}`]) {
      this.glideInstances.push(
        new Glide(this.$refs[`glide_${productId}`], {
          type: 'carousel',
          keyboard: false,
        }).mount(),
      );
    }
  },

  handleGridLayoutChange(event) {
    // When grid layout changes, update all sliders
    const { mobileColumns, desktopColumns, initialLoad } = event.detail;

    // Update image sizes
    this.updateImageSizes(mobileColumns, desktopColumns);

    // Update all sliders
    this.glideInstances.forEach((instance) => {
      instance.update();
    });

    // If this is the initial load, don't trigger the fade-in animation
    if (initialLoad) {
      document.dispatchEvent(new CustomEvent('grid-layout-update-complete'));
      return;
    }

    // Create a promise that resolves when all sliders are updated
    const updatePromises = this.glideInstances.map((instance) => {
      return new Promise((resolve) => {
        // Update the slider
        instance.update();

        // Resolve immediately since Glide doesn't have a reliable 'build.after' event
        resolve();
      });
    });

    // When all sliders are updated, dispatch an event
    Promise.all(updatePromises)
      .then(() => {
        // Add a small delay to ensure DOM has updated
        setTimeout(() => {
          document.dispatchEvent(
            new CustomEvent('grid-layout-update-complete'),
          );
        }, 50);
      })
      .catch(() => {
        // If there's an error, still dispatch the event
        document.dispatchEvent(new CustomEvent('grid-layout-update-complete'));
      });

    // If there are no sliders, still dispatch the event
    if (this.glideInstances.length === 0) {
      document.dispatchEvent(new CustomEvent('grid-layout-update-complete'));
    }
  },

  updateImageSizes(mobileColumns, desktopColumns) {
    // Find all images in this card
    const cardImages = this.$el.querySelectorAll('.card-image');

    if (cardImages.length === 0) return;

    const mobileSize = mobileColumns === 1 ? '100vw' : '50vw';
    const desktopSize = `${100 / desktopColumns}vw`;

    cardImages.forEach((img) => {
      img.sizes = `(min-width: 990px) ${desktopSize}, ${mobileSize}`;
    });
  },

  async setActiveProduct(product) {
    if (this.activeProduct?.id === product.id) {
      return;
    }
    this.activeProduct = product;
    await this.$nextTick();
    const existingSlider = this.glideInstances.find((instance) => {
      return (
        instance.selector.attributes['x-ref'].value === `glide_${product.id}`
      );
    });
    if (!existingSlider) {
      this.glideInstances.push(
        new Glide(this.$refs[`glide_${this.activeProduct.id}`], {
          type: 'carousel',
          keyboard: false,
        }).mount(),
      );
    } else {
      existingSlider.update();
    }
  },

  handleSlideNav(direction, glideId) {
    const glideInstance = this.getGlideInstance(glideId);
    if (direction === 'previous') {
      glideInstance.go('<');
    } else {
      glideInstance.go('>');
    }
  },

  getGlideInstance(glideId) {
    return this.glideInstances.find((glideInstance) => {
      return glideInstance.selector.attributes['x-ref'].value === glideId;
    });
  },

  handleWishlistToggle({ productId }) {
    this.$store.main.showWishlistSelectVariantModal(productId)
  },
});
